import { useCallback, useEffect, useMemo, useState } from "react";

import {
  formDraftStorage,
  type StoredFormDraft,
} from "@/lib/form-draft-storage";

type DraftIdentity = {
  projectId: string;
  formId: string;
  version: number;
};

interface UseFormDraftPersistenceOptions {
  /** When false, draft loading and saving is completely disabled (e.g., for preview mode) */
  enabled?: boolean;
}

interface UseFormDraftPersistenceReturn {
  draft: StoredFormDraft | null;
  isRestoringDraft: boolean;
  saveDraft: (draft: StoredFormDraft) => void;
  clearDraft: () => void;
}

export function useFormDraftPersistence(
  identity?: DraftIdentity,
  options?: UseFormDraftPersistenceOptions,
): UseFormDraftPersistenceReturn {
  const enabled = options?.enabled ?? true;
  const [draft, setDraft] = useState<StoredFormDraft | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  const identityKey = useMemo(() => {
    if (!identity) {
      return null;
    }
    return `${identity.projectId}:${identity.formId}:${identity.version}`;
  }, [identity]);

  const isRestoringDraft = enabled && Boolean(identityKey && loadedKey !== identityKey);

  useEffect(() => {
    // Skip draft loading when disabled (e.g., preview mode)
    if (!enabled) {
      setDraft(null);
      setLoadedKey(null);
      return;
    }

    if (!identity || !identityKey) {
      setDraft(null);
      setLoadedKey(null);
      return;
    }

    if (loadedKey === identityKey) {
      return;
    }

    const stored = formDraftStorage.get(
      identity.projectId,
      identity.formId,
      identity.version,
    );
    setDraft(stored);
    setLoadedKey(identityKey);
  }, [enabled, identity, identityKey, loadedKey]);

  const saveDraft = useCallback((nextDraft: StoredFormDraft) => {
    if (!enabled) {
      return;
    }
    formDraftStorage.set(nextDraft);
    setDraft(nextDraft);
    setLoadedKey(
      `${nextDraft.projectId}:${nextDraft.formId}:${nextDraft.version}`,
    );
  }, [enabled]);

  const clearDraft = useCallback(() => {
    if (!enabled) {
      setDraft(null);
      setLoadedKey(identityKey);
      return;
    }
    if (!identity) {
      setDraft(null);
      setLoadedKey(null);
      return;
    }
    formDraftStorage.clear(identity.projectId, identity.formId, identity.version);
    setDraft(null);
    setLoadedKey(`${identity.projectId}:${identity.formId}:${identity.version}`);
  }, [enabled, identity, identityKey]);

  return {
    draft,
    isRestoringDraft,
    saveDraft,
    clearDraft,
  };
}
